// WASM binding for kolilang.wasm. Loads the module, marshals UTF-8 strings
// into linear memory, decodes the 12-byte-header + JSON result envelope.
//
// Ownership contract (must match src/wasm_main.zig):
//
//   JS allocates:  input buffer (N bytes) + out_len cell (4 bytes).
//   JS fills:      input buffer with UTF-8 source bytes.
//   JS calls:      kolilang_lex / kolilang_parse, gets result pointer back.
//   Zig writes:    result length into *out_len.
//   JS reads:      header + JSON payload from result.
//   JS frees:      input, out_len cell, and the result.

export interface Token {
  k: string;
  s: number;
  e: number;
}

export interface LexResult {
  tokens: Token[];
  total: number;
  invalid: number;
}

export interface Diagnostic {
  msg: string;
  s: number;
  e: number;
  sev: 'error' | 'warning' | 'info';
}

export interface StmtMeta {
  kind: string;
  /** Lowercase PROC name — present only when kind === 'proc_call'. */
  name?: string;
  /** Licensing tier from src/proc_tiers.zig — present only when
   *  kind === 'proc_call'. */
  tier?: 'free' | 'paid';
  s: number;
  e: number;
}

/** Per-file `proc <name>` frequency, counted over the RAW token stream
 *  (pre macro expansion) so procs inside %macro definition bodies count
 *  even when the macro is never invoked in-file. */
export interface ProcCount {
  /** Lowercase proc name. */
  name: string;
  /** Licensing tier from src/proc_tiers.zig. */
  tier: 'free' | 'paid';
  count: number;
}

export interface ParseResult {
  stmts: StmtMeta[];
  diagnostics: Diagnostic[];
  procs: ProcCount[];
  stmtCount: number;
  diagCount: number;
  /** Total non-EOF tokens produced by the lexer (wasm_main emits this
   *  alongside the parse output so the web side avoids a separate lex()
   *  round-trip per file). */
  tokens: number;
  /** Count of tokens classified as invalid by the lexer. */
  invalid: number;
}

export interface PlanStep {
  /** `sql` executes against DuckDB-WASM; `log` and `err` render as text. */
  kind: 'sql' | 'log' | 'err';
  text: string;
}

export interface PlanResult {
  steps: PlanStep[];
  /** Parse diagnostics that fired while building the plan. Spans reference
   *  the original (post-macro-expansion) source. */
  diagnostics: Pick<Diagnostic, 'msg' | 's' | 'e'>[];
}

export interface RunResult {
  /** Concatenated stdout (PROC PRINT, NOTE: lines, table renders). */
  out: string;
  /** Concatenated stderr (ERROR:, WARNING: lines). */
  err: string;
  /** Parser diagnostics accumulated across macro-expanded chunks. */
  diagnostics: Pick<Diagnostic, 'msg' | 's' | 'e'>[];
}

export interface Kolilang {
  version(): string;
  lex(source: string): LexResult;
  parse(source: string): ParseResult;
  plan(source: string): PlanResult;
  /** Full evaluator. Runs in a worker that owns its own kolilang.wasm
   *  instance and bridges the wasm host imports onto DuckDB-wasm via
   *  a SharedArrayBuffer + Atomics.wait protocol. Tables stay JS-side
   *  in the shared DuckDB-wasm session; this returns only the log
   *  envelope. */
  run(source: string): Promise<RunResult>;
}

// WASM ABI surface (Exports, HostImports, header constants) is declared
// canonically in `wasm-abi.ts`. Importing from there keeps wasm.ts and
// wasm-bridge.worker.ts from drifting against each other or against the
// Zig side.
import {
  type KolilangExports,
  type HostImports,
  HEADER_SIZE,
  HEADER_MAGIC,
  HEADER_VERSION,
  throwingHostImports,
} from './wasm-abi';

export type { HostImports };

// Reused across every lex/parse call — TextEncoder/TextDecoder construction
// is surprisingly hot in the StreamLanguage token path.
const encoder = new TextEncoder();
const decoder = new TextDecoder();

// `?url` asks Vite to treat the wasm as an asset: in production it's
// emitted as /assets/kolilang-<contenthash>.wasm, which automatically
// busts browser caches whenever the binary changes.
// eslint-disable-next-line import/no-unresolved
import kolilangWasmUrl from './assets/kolilang.wasm?url';

export async function loadKolilang(url?: string, hostImports?: HostImports): Promise<Kolilang> {
  const wasmUrl = url ?? kolilangWasmUrl;
  const env = hostImports ?? defaultHostImports();
  const { instance } = await WebAssembly.instantiateStreaming(fetch(wasmUrl), { env });
  const exp = instance.exports as unknown as KolilangExports;

  // Spin up the runner worker eagerly so the first `run()` call doesn't
  // pay for both worker startup and DuckDB-wasm init at click time.
  // The worker requires cross-origin isolation (SharedArrayBuffer);
  // when that's missing — typically a dev session without the COOP/COEP
  // headers — we fall back to a runner that surfaces the limitation
  // through a clear error instead of failing at instantiation.
  const runner = startRunnerSafely();
  return new KolilangImpl(exp, runner);
}

function startRunnerSafely(): Promise<RunRunner> {
  if (typeof SharedArrayBuffer === 'undefined' || !globalThis.crossOriginIsolated) {
    return Promise.resolve({
      run: async () => {
        throw new Error('kolilang.run requires cross-origin isolation (COOP/COEP headers). The page is not crossOriginIsolated.');
      },
    });
  }
  // Lazy-load the bridge so pages that never call run() don't pay for
  // the DuckDB-wasm + worker bundle. Vite handles the dynamic import
  // chunk-splitting automatically.
  return import('./wasm-bridge').then((m) => m.createRunner());
}

interface RunRunner {
  run(source: string): Promise<RunResult>;
}

/// Stub host imports that throw if invoked — used by lex/parse/plan
/// callers that never invoke kolilang_run on the main thread. The actual
/// run path lives in the worker (wasm-bridge.worker.ts) and wires real
/// implementations against DuckDB-wasm. The required name list is
/// canonical in wasm-abi.ts so adding a new host import doesn't drift
/// silently against `src/duckdb_wasm.zig`.
function defaultHostImports(): HostImports {
  return throwingHostImports('kolilang');
}

class KolilangImpl implements Kolilang {
  // Memory can be moved when linear memory grows, so we re-read mem.buffer
  // for every view. Never cache a Uint8Array across an exported call.
  constructor(
    private readonly exp: KolilangExports,
    private readonly runnerPromise: Promise<RunRunner>,
  ) {}

  version(): string {
    const ptr = this.exp.kolilang_version();
    return readCString(this.exp.memory, ptr);
  }

  lex(source: string): LexResult {
    return this.invoke(source, this.exp.kolilang_lex) as LexResult;
  }

  parse(source: string): ParseResult {
    return this.invoke(source, this.exp.kolilang_parse) as ParseResult;
  }

  plan(source: string): PlanResult {
    return this.invoke(source, this.exp.kolilang_plan) as PlanResult;
  }

  async run(source: string): Promise<RunResult> {
    const runner = await this.runnerPromise;
    return runner.run(source);
  }

  private invoke(source: string, fn: (p: number, n: number, o: number) => number): unknown {
    const exp = this.exp;
    const bytes = encoder.encode(source);
    const inPtr = exp.kolilang_alloc(bytes.length || 1);
    if (inPtr === 0) throw new Error('kolilang_alloc failed (input)');
    const outLenPtr = exp.kolilang_alloc(4);
    if (outLenPtr === 0) {
      exp.kolilang_free(inPtr, bytes.length || 1);
      throw new Error('kolilang_alloc failed (out_len)');
    }
    try {
      new Uint8Array(exp.memory.buffer, inPtr, bytes.length).set(bytes);
      const resultPtr = fn(inPtr, bytes.length, outLenPtr);
      if (resultPtr === 0) throw new Error('kolilang call failed');
      const total = new Uint32Array(exp.memory.buffer, outLenPtr, 1)[0];
      return decodeResult(exp, resultPtr, total);
    } finally {
      exp.kolilang_free(inPtr, bytes.length || 1);
      exp.kolilang_free(outLenPtr, 4);
    }
  }
}

function decodeResult(exp: KolilangExports, ptr: number, total: number): unknown {
  const view = new DataView(exp.memory.buffer, ptr, total);
  const magic = view.getUint32(0, true);
  if (magic !== HEADER_MAGIC) {
    exp.kolilang_free_result(ptr, total);
    throw new Error(`kolilang: bad result magic 0x${magic.toString(16)}`);
  }
  const version = view.getUint32(4, true);
  if (version !== HEADER_VERSION) {
    exp.kolilang_free_result(ptr, total);
    throw new Error(`kolilang: unsupported result version ${version} (host expects ${HEADER_VERSION})`);
  }
  const payloadLen = view.getUint32(8, true);
  // slice() copies so we don't hold a reference into WASM memory past the free.
  const payload = new Uint8Array(exp.memory.buffer, ptr + HEADER_SIZE, payloadLen).slice();
  exp.kolilang_free_result(ptr, total);
  return JSON.parse(decoder.decode(payload));
}

function readCString(mem: WebAssembly.Memory, ptr: number): string {
  const bytes = new Uint8Array(mem.buffer, ptr);
  let end = 0;
  while (end < bytes.length && bytes[end] !== 0) end += 1;
  return decoder.decode(bytes.subarray(0, end));
}
